'use client'

import { createContext, type ReactNode, useContext, useEffect, useState } from 'react'

import { useCheckCleanlabAdmin } from '@/api/admin'
import { useGetOrganizationPermissions } from '@/api/organizations'
import { useMyself, useUserOrganizations } from '@/api/users'

interface UserContextType {
  selectedOrganizationId: string
  setSelectedOrganizationId: (id: string) => void
  isLoading: boolean
  userData: ReturnType<typeof useMyself>['data']
  error: Error | null
  isCleanlabAdmin?: boolean
  isOrganizationAdmin: boolean
}

const UserContext = createContext<UserContextType | undefined>(undefined)

export function UserContextProvider({ children }: { children: ReactNode }) {
  const [selectedOrganizationId, setSelectedOrganizationId] = useState<string>('')

  const { data: userData, error: userError, isLoading: isLoadingUser } = useMyself()

  const {
    data: userOrganizations,
    error: orgsError,
    isLoading: isLoadingOrgs,
  } = useUserOrganizations({
    enabled: !!userData && !!userData.email_verified,
  })

  useEffect(() => {
    if (!userData || !userOrganizations) return
    if (userOrganizations.organizations?.[0]?.organization_id) {
      setSelectedOrganizationId(userOrganizations.organizations[0].organization_id)
    }
  }, [userData, userOrganizations])

  const { data: isCleanlabAdmin, isLoading: isLoadingIsCleanlabAdmin } = useCheckCleanlabAdmin({
    enabled: !!userOrganizations,
  })

  const { data: organizationPermissions } = useGetOrganizationPermissions(selectedOrganizationId, {
    enabled: !!userOrganizations && !!selectedOrganizationId,
  })

  return (
    <UserContext.Provider
      value={{
        selectedOrganizationId,
        setSelectedOrganizationId,
        isLoading: isLoadingOrgs || isLoadingUser || isLoadingIsCleanlabAdmin,
        userData,
        error: userError || orgsError || null,
        isCleanlabAdmin,
        isOrganizationAdmin: organizationPermissions?.is_admin ?? false,
      }}
    >
      {children}
    </UserContext.Provider>
  )
}

export function useUser() {
  const context = useContext(UserContext)
  if (context === undefined) {
    throw new Error('useUser must be used within a UserContextProvider')
  }
  return context
}
